import {
    Viewer,
    PostProcessStage,
    Color,
    Cesium3DTileset,
    CustomShader,
    CustomShaderMode,
    LightingModel,
    UniformType,
    Cartesian3,
} from "cesium";

// ─────────────────────────────────────────────────────────────────────
//  Kiểu dữ liệu công khai
// ─────────────────────────────────────────────────────────────────────

export type EdgeMode = "postprocess" | "shader" | "wireframe";

export interface EdgeRenderingOptions {
    /** Chế độ hiển thị – mặc định: `'postprocess'` */
    mode?: EdgeMode;
    /** Màu cạnh – mặc định: `Color.BLACK` */
    color?: Color;
    /** Độ dày cạnh (pixel) – mặc định: `1.5` */
    thickness?: number;
    /**
     * Độ nhạy phát hiện cạnh giữa 2 mặt phẳng (1–10).
     * - `1`  = chỉ hiển thị cạnh sắc nét nhất (≥90°)
     * - `5`  = hiển thị thêm cạnh góc vừa
     * - `10` = rất nhạy
     *
     * Mặc định: `1`.
     */
    strength?: number;
    /** Độ nhạy viền silhouette (depth-based) – mặc định: `1.0` */
    depthStrength?: number;
    /** Độ trong suốt lớp cạnh (0–1) – mặc định: `1.0` */
    opacity?: number;
    /** Khoảng cách tham chiếu (meters) mà tại đó độ dày cạnh khớp chính xác với `thickness` - mặc định: `150.0` */
    referenceDepth?: number;
    /** Tỉ lệ phóng to tối đa khi camera ở cực kỳ gần - mặc định: `2.5` */
    maxThicknessScale?: number;
    /** Tỉ lệ thu nhỏ tối thiểu khi camera ở cực kỳ xa - mặc định: `0.4` */
    minThicknessScale?: number;
}

// ─────────────────────────────────────────────────────────────────────
//  GLSL – Normal-reconstruction Sobel (KHÔNG có depth silhouette)
//
//  Nguyên lý:
//    1. Đọc depth → tái dựng eye-space position cho 9 điểm lân cận
//    2. Cross product 3 điểm → surface normal tại mỗi điểm
//    3. Sobel filter trên normal field → gradient lớn = cạnh giữa 2 mặt
//    4. Dùng hàm bình phương (e²) để khuếch đại cạnh sắc (90°+)
//       và triệt tiêu thay đổi normal mờ dần (silhouette bề mặt cong)
//
//  Cesium built-in được dùng:
//    czm_viewport                          vec4  – kích thước viewport
//    czm_projection                        mat4  – projection matrix
//    czm_oneOverLog2FarDepthFromNearPlusOne float – đảo log-depth
// ─────────────────────────────────────────────────────────────────────
export const NORMAL_EDGE_SHADER = /* glsl */ `
uniform sampler2D colorTexture;
uniform sampler2D depthTexture;
uniform float edgeThickness;
uniform vec4  edgeColor;
uniform float edgeStrength;
uniform float referenceDepth;
uniform float maxThicknessScale;
uniform float minThicknessScale;

in vec2 v_textureCoordinates;

// ── Đọc raw depth ─────────────────────────────────────────────────
float rawDepth(vec2 uv) {
    return texture(depthTexture, uv).r;
}

// ── Log-depth → eye-space Z distance ─────────────────────────────
float logDepthToEyeZ(float d) {
    if (d <= 0.0) return 0.0;
    return exp2(d / czm_oneOverLog2FarDepthFromNearPlusOne) - 1.0;
}

// ── Tái dựng eye-space position ───────────────────────────────────
//   x_eye = ndc.x * eyeZ / czm_projection[0][0]  (projection focal_x)
//   y_eye = ndc.y * eyeZ / czm_projection[1][1]
vec3 getEyePos(vec2 uv) {
    float d = rawDepth(uv);
    if (d <= 0.0) return vec3(0.0);
    float eyeZ = logDepthToEyeZ(d);
    vec2  ndc  = uv * 2.0 - 1.0;
    return vec3(ndc.x * eyeZ / czm_projection[0][0],
                ndc.y * eyeZ / czm_projection[1][1],
                -eyeZ);
}

// ── Tái dựng surface normal từ cross product 3 lân cận ───────────
vec3 surfaceNormal(vec2 uv, vec2 ts) {
    vec3 c = getEyePos(uv);
    vec3 r = getEyePos(uv + vec2(ts.x, 0.0));
    vec3 u = getEyePos(uv + vec2(0.0,  ts.y));
    if (length(c) == 0.0 || length(r) == 0.0 || length(u) == 0.0)
        return vec3(0.0, 0.0, 1.0);
    return normalize(cross(r - c, u - c));
}

void main() {
    vec4 base = texture(colorTexture, v_textureCoordinates);

    // Bỏ qua pixel nền (sky)
    float d_c = rawDepth(v_textureCoordinates);
    if (d_c <= 0.0) {
        out_FragColor = base;
        return;
    }

    float depth_c = logDepthToEyeZ(d_c);
    // Cố định kích thước (fixed pixel size in screen space) để đảm bảo đường vẽ luôn siêu mảnh ("nhuyễn")
    float dynamicThickness = edgeThickness * 0.75;
    vec2 ts   = vec2(dynamicThickness) / czm_viewport.zw;

    // Dùng baseline cố định 3.0px để tái dựng normal mượt mà, triệt tiêu răng cưa nhiễu depth trên mặt cong
    vec2 normalBaseline = vec2(3.0) / czm_viewport.zw;
    vec2 nts = ts * 1.5;

    // ── Lấy mẫu normal tại 8 điểm lân cận ───────────────────────
    vec3 n_tl = surfaceNormal(v_textureCoordinates + vec2(-nts.x,  nts.y), normalBaseline);
    vec3 n_t  = surfaceNormal(v_textureCoordinates + vec2( 0.0,    nts.y), normalBaseline);
    vec3 n_tr = surfaceNormal(v_textureCoordinates + vec2( nts.x,  nts.y), normalBaseline);
    vec3 n_l  = surfaceNormal(v_textureCoordinates + vec2(-nts.x,  0.0  ), normalBaseline);
    vec3 n_r  = surfaceNormal(v_textureCoordinates + vec2( nts.x,  0.0  ), normalBaseline);
    vec3 n_bl = surfaceNormal(v_textureCoordinates + vec2(-nts.x, -nts.y), normalBaseline);
    vec3 n_b  = surfaceNormal(v_textureCoordinates + vec2( 0.0,   -nts.y), normalBaseline);
    vec3 n_br = surfaceNormal(v_textureCoordinates + vec2( nts.x, -nts.y), normalBaseline);

    // ── Toán tử Sobel trên normal field ──────────────────────────
    vec3 ngx = -n_tl - 2.0*n_l - n_bl + n_tr + 2.0*n_r + n_br;
    vec3 ngy = -n_tl - 2.0*n_t - n_tr + n_bl + 2.0*n_b + n_br;
    float normalGrad = sqrt(dot(ngx, ngx) + dot(ngy, ngy));

    // ── Chuyển gradient → edge intensity ─────────────────────────
    // Hệ số chuẩn hóa 6.0: Sobel tối đa khi hai normal vuông góc ≈ 5.66
    // edgeStrength điều chỉnh ngưỡng: strength=1 → chỉ cạnh ≥90°
    float e    = clamp(normalGrad / (6.0 / max(edgeStrength, 0.01)), 0.0, 1.0);
    // Áp dụng smoothstep để làm mềm biên đường cong, triệt tiêu răng cưa và làm mịn nét vẽ
    float edge = smoothstep(0.0, 1.0, e * e);

    // Tính toán độ mờ dần theo khoảng cách camera (gần thì thấy, xa thì mất)
    float nearDepth = referenceDepth * minThicknessScale;
    float farDepth = referenceDepth * maxThicknessScale;
    float fade = smoothstep(farDepth, nearDepth, depth_c);

    out_FragColor = mix(base, vec4(edgeColor.rgb, 1.0), edge * edgeColor.a * fade);
}
`;

// ─────────────────────────────────────────────────────────────────────
//  GLSL – Kết hợp Depth Sobel + Normal-Reconstruction Sobel
//
//  Nguyên lý:
//    1. Depth Sobel  → phát hiện silhouette (biên giữa vật và nền)
//    2. Normal Sobel → tái dựng vector pháp tuyến mặt từ depth buffer
//                      rồi áp Sobel → phát hiện cạnh giữa 2 mặt phẳng
//  Kết hợp cả hai cho thấy đầy đủ các cạnh của vật thể (như ArcGIS Scene Layer edges).
//
//  Cesium built-in uniforms được dùng:
//    czm_viewport                         vec4  – kích thước viewport
//    czm_projection                       mat4  – projection matrix
//    czm_oneOverLog2FarDepthFromNearPlusOne float – dùng để đảo log-depth
// ─────────────────────────────────────────────────────────────────────
const COMBINED_EDGE_SHADER = /* glsl */ `
uniform sampler2D colorTexture;
uniform sampler2D depthTexture;
uniform float edgeThickness;
uniform vec4  edgeColor;
uniform float normalStrength;
uniform float depthStrength;
uniform float referenceDepth;
uniform float maxThicknessScale;
uniform float minThicknessScale;

in vec2 v_textureCoordinates;

// ── Đọc raw depth từ texture ──────────────────────────────────────
float rawDepth(vec2 uv) {
    return texture(depthTexture, uv).r;
}

// ── Chuyển log-encoded depth → eye-space distance (meters) ───────
//   Cesium dùng logarithmic depth mặc định (LOG_DEPTH).
//   czm_oneOverLog2FarDepthFromNearPlusOne = 1 / log2(far - near + 1)
//   → d_log = log2(eyeZ + 1) * oneOverLog2FarPlus1
//   → eyeZ  = exp2(d_log / oneOverLog2FarPlus1) - 1
float logDepthToEyeZ(float d) {
    if (d <= 0.0) return 0.0;
    return exp2(d / czm_oneOverLog2FarDepthFromNearPlusOne) - 1.0;
}

// ── Tái dựng eye-space position từ UV và raw depth ────────────────
//   Dùng các phần tử của czm_projection thay vì inverse để tránh lỗi precision.
//   Với perspective projection chuẩn:
//     x_eye = ndc.x * (-eyeZ) / czm_projection[0][0]
//     y_eye = ndc.y * (-eyeZ) / czm_projection[1][1]
//   (z_eye âm theo convention OpenGL vì camera nhìn theo -Z)
vec3 getEyePos(vec2 uv) {
    float d = rawDepth(uv);
    if (d <= 0.0) return vec3(0.0);   // nền/sky – không có geometry

    float eyeZ = logDepthToEyeZ(d);   // khoảng cách từ camera (dương)
    vec2  ndc  = uv * 2.0 - 1.0;

    float x = ndc.x * eyeZ / czm_projection[0][0];
    float y = ndc.y * eyeZ / czm_projection[1][1];
    return vec3(x, y, -eyeZ);         // eye-space: +Z ra sau lưng camera
}

// ── Đọc depth tuyến tính ở eye-space ──────────────────────────────
float getEyeDepth(vec2 uv) {
    float d = rawDepth(uv);
    if (d <= 0.0) return 100000.0;     // Nền trời ở vô cực
    return logDepthToEyeZ(d);
}

// ── Tái dựng normal mặt phẳng tại điểm uv từ các điểm lân cận hợp lệ ───
//   Sử dụng giải pháp tự động chuyển hướng tìm điểm khi gặp biên hoặc nền trời,
//   giúp loại bỏ hoàn toàn nhiễu và vạch đen bao quanh silhouette ngoài.
vec3 surfaceNormal(vec2 uv, vec2 ts) {
    vec3 c = getEyePos(uv);
    float depth_c = -c.z;
    if (depth_c <= 0.0) return vec3(0.0, 0.0, 1.0);

    vec3 dx = vec3(0.0);
    vec3 dy = vec3(0.0);

    // Tính dx (phương ngang) - kiểm tra bên phải (R), nếu lệch depth/sky thì dùng bên trái (L)
    vec3 r = getEyePos(uv + vec2(ts.x, 0.0));
    float depth_r = -r.z;
    if (depth_r > 0.0 && abs(depth_r - depth_c) <= depth_c * 0.05) {
        dx = r - c;
    } else {
        vec3 l = getEyePos(uv - vec2(ts.x, 0.0));
        float depth_l = -l.z;
        if (depth_l > 0.0 && abs(depth_l - depth_c) <= depth_c * 0.05) {
            dx = c - l;
        }
    }

    // Tính dy (phương dọc) - kiểm tra bên trên (U), nếu lệch depth/sky thì dùng bên dưới (D)
    vec3 u = getEyePos(uv + vec2(0.0, ts.y));
    float depth_u = -u.z;
    if (depth_u > 0.0 && abs(depth_u - depth_c) <= depth_c * 0.05) {
        dy = u - c;
    } else {
        vec3 d = getEyePos(uv - vec2(0.0, ts.y));
        float depth_d = -d.z;
        if (depth_d > 0.0 && abs(depth_d - depth_c) <= depth_c * 0.05) {
            dy = c - d;
        }
    }

    if (length(dx) == 0.0 || length(dy) == 0.0) {
        return vec3(0.0, 0.0, 1.0);
    }
    return normalize(cross(dx, dy));
}

// ── Tái dựng normal có trọng số theo depth ─────────────────────────
//   Nếu điểm lân cận vượt quá ngưỡng depth (ví dụ silhouette biên ngoài),
//   ta sẽ gán normal của nó bằng n_c để hiệu quả gradient Sobel triệt tiêu về 0.
vec3 getWeightedNormal(vec2 uv_n, vec3 n_c, float depth_c, vec2 nts) {
    float depth_n = -getEyePos(uv_n).z;
    if (depth_n <= 0.0) return n_c;
    float weight = smoothstep(depth_c * 0.05, depth_c * 0.01, abs(depth_c - depth_n));
    vec3 n_raw = surfaceNormal(uv_n, nts);
    return mix(n_c, n_raw, weight);
}

void main() {
    vec4 base = texture(colorTexture, v_textureCoordinates);

    // Bỏ qua pixel nền
    float dc = rawDepth(v_textureCoordinates);
    if (dc <= 0.0) {
        out_FragColor = base;
        return;
    }

    // Lấy thông tin depth và normal của pixel trung tâm
    vec3 pos_c = getEyePos(v_textureCoordinates);
    float depth_c = -pos_c.z;

    // Cố định sample offset ở 1.0px để có độ nét cao nhất, bám sát vật thể và loại bỏ hoàn toàn răng cưa/mờ nhòe subpixel
    vec2 ts = vec2(1.0) / czm_viewport.zw;

    // Dùng baseline cố định 1.0px để tái dựng normal cục bộ chính xác, giúp đường vẽ bám khít chặt chẽ vào mô hình
    vec2 normalBaseline = vec2(1.0) / czm_viewport.zw;
    vec3 n_c = surfaceNormal(v_textureCoordinates, normalBaseline);

    // ════════════════════════════════════════════════════════════
    //  1. DEPTH RATIO SILHOUETTE – phát hiện viền bao ngoài thực sự
    //     Sử dụng tỷ lệ độ sâu hướng về phía xa ở 4 hướng trực giao để vẽ silhouette cực kỳ nét (đúng 1-pixel).
    // ════════════════════════════════════════════════════════════
    float depthEdge = 0.0;
    if (depthStrength > 0.0) {
        float threshold = 0.1 / max(depthStrength, 0.001);
        
        float ed_l = getEyeDepth(v_textureCoordinates + vec2(-ts.x,  0.0));
        float ed_r = getEyeDepth(v_textureCoordinates + vec2( ts.x,  0.0));
        float ed_t = getEyeDepth(v_textureCoordinates + vec2( 0.0,   ts.y));
        float ed_d = getEyeDepth(v_textureCoordinates + vec2( 0.0,  -ts.y));

        float maxRatio = 0.0;
        maxRatio = max(maxRatio, ed_l / depth_c - 1.0);
        maxRatio = max(maxRatio, ed_r / depth_c - 1.0);
        maxRatio = max(maxRatio, ed_t / depth_c - 1.0);
        maxRatio = max(maxRatio, ed_d / depth_c - 1.0);

        depthEdge = smoothstep(0.0, threshold * 0.3, maxRatio - threshold);
    }

    // ════════════════════════════════════════════════════════════
    //  2. NORMAL EDGE – phát hiện cạnh trong siêu mảnh đúng 1-pixel
    //     Sử dụng sai phân một phía sang Phải (Right) và Dưới (Down) để có nét vẽ mỏng tối đa.
    // ════════════════════════════════════════════════════════════
    vec3 n_r = getWeightedNormal(v_textureCoordinates + vec2(ts.x, 0.0), n_c, depth_c, normalBaseline);
    vec3 n_d = getWeightedNormal(v_textureCoordinates + vec2(0.0, -ts.y), n_c, depth_c, normalBaseline);

    float diffX = 1.0 - clamp(dot(n_c, n_r), 0.0, 1.0);
    float diffY = 1.0 - clamp(dot(n_c, n_d), 0.0, 1.0);
    float normalGrad = max(diffX, diffY);
    
    // Tăng cường độ nhạy với hệ số 3.0 để nét vẽ mảnh 1-pixel vẫn hiển thị rõ ràng, sắc nét
    float e = clamp(normalGrad * normalStrength * 3.0, 0.0, 1.0);
    float normalEdge = smoothstep(0.0, 1.0, e * e);

    // ════════════════════════════════════════════════════════════
    //  3. Kết hợp và tính toán độ mờ dần
    // ════════════════════════════════════════════════════════════
    float edge = max(depthEdge, normalEdge);

    // Tính toán độ mờ dần theo khoảng cách camera (gần thì thấy, xa thì mất)
    float nearDepth = referenceDepth * minThicknessScale;
    float farDepth = referenceDepth * maxThicknessScale;
    float fade = smoothstep(farDepth, nearDepth, depth_c);

    out_FragColor = mix(base, vec4(edgeColor.rgb, 1.0), edge * edgeColor.a * fade * clamp(edgeThickness, 0.0, 2.0));
}
`;

// ─────────────────────────────────────────────────────────────────────
//  GLSL – CustomShader: fwidth(normal) cho per-tileset shader mode
//
//  ⚠️  KHÔNG khai báo lại uniform ở đây.
//      Cesium tự động inject các uniform từ CustomShader.uniforms vào shader.
// ─────────────────────────────────────────────────────────────────────
const NORMAL_FWIDTH_FRAG = /* glsl */ `
void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
#ifdef HAS_NORMALS
    vec3  n          = fsInput.attributes.normalEC;
    float edgeFactor = length(fwidth(n)) * u_edgeStrength;
    edgeFactor       = clamp(edgeFactor, 0.0, 1.0);
    material.diffuse = mix(material.diffuse, u_edgeColor, edgeFactor);
#endif
}
`;

// ─────────────────────────────────────────────────────────────────────
//  Lớp chính TileEdgeRenderer
// ─────────────────────────────────────────────────────────────────────

/**
 * Quản lý hiển thị cạnh (edge overlay) cho 3D Tiles trong Cesium.
 *
 * Sử dụng kết hợp:
 *  - **Depth Sobel**  → viền ngoài vật thể (silhouette)
 *  - **Normal Sobel** → cạnh giữa các mặt phẳng (box edges, floor–wall edges, v.v.)
 *
 * @example
 * ```typescript
 * const er = new TileEdgeRenderer(viewer);
 *
 * // Bật với giá trị mặc định
 * er.enable();
 *
 * // Tùy chỉnh
 * er.enable({ color: Color.BLACK, strength: 4, depthStrength: 20 });
 *
 * // Tắt
 * er.disable();
 * ```
 */
export class TileEdgeRenderer {
    private readonly _viewer: Viewer;
    private _postStage: PostProcessStage | null = null;
    private readonly _tilesetOrigShader = new Map<Cesium3DTileset, CustomShader | undefined>();
    private _selectedPrimitives: any[] = [];

    private _opts: Required<EdgeRenderingOptions> = {
        mode: "postprocess",
        color: Color.BLACK,
        thickness: 0.8,
        strength: 1.0,
        depthStrength: 1.0,
        opacity: 0.7,
        referenceDepth: 150.0,
        maxThicknessScale: 1.0,
        minThicknessScale: 0.3,
    };

    constructor(viewer: Viewer) {
        this._viewer = viewer;
    }

    // ── Trạng thái ────────────────────────────────────────────────

    get isEnabled(): boolean {
        return this._postStage !== null || this._tilesetOrigShader.size > 0;
    }

    // ── API toàn scene ────────────────────────────────────────────

    /**
     * Bật hiển thị cạnh toàn scene (Post-Processing: Depth + Normal Sobel).
     *
     * - **`strength`** (2–10): Độ nhạy cạnh giữa mặt phẳng (normal-based). Mặc định: 4.
     * - **`depthStrength`** (10–60): Độ nhạy viền silhouette (depth-based). Mặc định: 20.
     * - **`thickness`** (1–3): Độ dày pixel. Mặc định: 1.5.
     */
    enable(options: EdgeRenderingOptions = {}): void {
        this._opts = { ...this._opts, ...options };
        this._activatePostProcess();
    }

    /** Tắt lớp post-processing cạnh. */
    disablePostProcess(): void {
        this._deactivatePostProcess();
    }

    /** Bật/Tắt hiển thị cạnh toàn scene. */
    toggle(options?: EdgeRenderingOptions): void {
        if (this._postStage) {
            this._deactivatePostProcess();
        } else {
            this.enable(options);
        }
    }

    // ── API theo từng tileset ─────────────────────────────────────

    /**
     * Bật hiển thị cạnh trên một tileset cụ thể.
     *
     * - `mode: 'shader'`    – CustomShader fwidth(normal). Tốt cho BIM flat-shading.
     * - `mode: 'wireframe'` – debugWireframe native (tất cả tam giác).
     */
    enableOnTileset(tileset: Cesium3DTileset, options: EdgeRenderingOptions = {}): void {
        this._opts = { ...this._opts, ...options };
        const mode = options.mode ?? "shader";
        if (mode === "wireframe") {
            this._activateWireframe(tileset);
        } else {
            this._activateShader(tileset);
        }
    }

    /** Tắt hiển thị cạnh trên tileset và khôi phục shader gốc. */
    disableOnTileset(tileset: Cesium3DTileset): void {
        if (this._tilesetOrigShader.has(tileset)) {
            tileset.customShader = this._tilesetOrigShader.get(tileset);
            (tileset as any).debugWireframe = false;
            this._tilesetOrigShader.delete(tileset);
        }
    }

    /** Tắt tất cả các chế độ (post-process + tileset). */
    disable(): void {
        this._deactivatePostProcess();
        this._tilesetOrigShader.forEach((orig, tileset) => {
            tileset.customShader = orig;
            (tileset as any).debugWireframe = false;
        });
        this._tilesetOrigShader.clear();
    }

    // ── Setters động ──────────────────────────────────────────────

    /** Cập nhật màu cạnh. */
    setColor(color: Color): void {
        this._opts.color = color;
        this._refreshTilesetShaders();
    }

    /** Cập nhật độ dày pixel (postprocess). */
    setThickness(thickness: number): void {
        this._opts.thickness = thickness;
    }

    /** Cập nhật độ nhạy normal-based edge. */
    setStrength(strength: number): void {
        this._opts.strength = strength;
        this._refreshTilesetShaders();
    }

    /** Cập nhật độ nhạy depth/silhouette edge. */
    setDepthStrength(depthStrength: number): void {
        this._opts.depthStrength = depthStrength;
    }

    /** Cập nhật độ trong suốt (0–1). */
    setOpacity(opacity: number): void {
        this._opts.opacity = Math.min(1, Math.max(0, opacity));
    }

    setSelectedPrimitives(primitives: any[]): void {
        this._selectedPrimitives = primitives;
        this._activatePostProcess();
    }

    destroy(): void {
        this.disable();
    }

    // ── Private ───────────────────────────────────────────────────

    private _activatePostProcess(): void {
        this._deactivatePostProcess();
        if (this._selectedPrimitives.length === 0) {
            return;
        }
        this._postStage = new PostProcessStage({
            name: "dpu_combined_edge_detection",
            fragmentShader: COMBINED_EDGE_SHADER,
            uniforms: {
                edgeThickness: () => this._opts.thickness,
                edgeColor: () => new Color(
                    this._opts.color.red,
                    this._opts.color.green,
                    this._opts.color.blue,
                    this._opts.opacity
                ),
                normalStrength: () => this._opts.strength,
                depthStrength: () => this._opts.depthStrength,
                referenceDepth: () => this._opts.referenceDepth,
                maxThicknessScale: () => this._opts.maxThicknessScale,
                minThicknessScale: () => this._opts.minThicknessScale,
            },
        });
        this._postStage.selected = this._selectedPrimitives;
        this._viewer.scene.postProcessStages.add(this._postStage);
    }

    private _deactivatePostProcess(): void {
        if (this._postStage) {
            this._viewer.scene.postProcessStages.remove(this._postStage);
            this._postStage = null;
        }
    }

    private _activateShader(tileset: Cesium3DTileset): void {
        if (!this._tilesetOrigShader.has(tileset)) {
            this._tilesetOrigShader.set(tileset, tileset.customShader);
        }
        tileset.customShader = this._buildNormalEdgeShader();
    }

    private _activateWireframe(tileset: Cesium3DTileset): void {
        if (!this._tilesetOrigShader.has(tileset)) {
            this._tilesetOrigShader.set(tileset, tileset.customShader);
        }
        (tileset as any).debugWireframe = true;
    }

    private _buildNormalEdgeShader(): CustomShader {
        const { color, strength } = this._opts;
        return new CustomShader({
            mode: CustomShaderMode.MODIFY_MATERIAL,
            lightingModel: LightingModel.PBR,
            uniforms: {
                u_edgeColor: {
                    type: UniformType.VEC3,
                    value: new Cartesian3(color.red, color.green, color.blue),
                },
                u_edgeStrength: {
                    type: UniformType.FLOAT,
                    value: strength,
                },
            },
            fragmentShaderText: NORMAL_FWIDTH_FRAG,
        });
    }

    private _refreshTilesetShaders(): void {
        this._tilesetOrigShader.forEach((_, tileset) => {
            if (!(tileset as any).debugWireframe) {
                tileset.customShader = this._buildNormalEdgeShader();
            }
        });
    }
}
